// "Top software": the programs behind the CVEs of an account's machines, so the software updates can be focused. Each
// machine's result lists its worst programs (cve.topSoftware, without the Windows operating system); here they are
// grouped by program family ("Microsoft .NET Runtime" is one row whatever the versions installed on the machines).
// Plain functions only (no React), so they can be unit tested.

// "Microsoft .NET Runtime - 8.0.31 (x64)" and "Microsoft .NET Runtime - 9.0.5 (x86)" are one family. Versions,
// architecture and language are taken off the end of the name; years in the middle of a name ("Visual C++ 2015-2022
// Redistributable") stay, because they are part of the product.
export const familyOf = (name) => {
  let s = String(name || '').trim();
  let before;
  do {
    before = s;
    s = s
      .replace(/\s*\((?:x64|x86|arm64|64-bit|32-bit)\)\s*$/i, '')
      .replace(/\s*[-–]\s*[a-z]{2}-[a-z]{2,4}\s*$/i, '')                 // "- en-us"
      .replace(/\s*[-–]?\s*\b(?:x64|x86|arm64|64-bit|32-bit)\b\s*$/i, '')
      .replace(/\s*[-–]?\s*\bv?\d+(?:\.\d+)+\S*\s*$/i, '')               // "8.0.31", "- 14.38.33135", "v1.2.3"
      .replace(/\s*[-–]\s*$/, '')
      .trim();
  } while (s !== before && s);
  return s || String(name || '').trim();
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// The programs one machine reports, as an array (an old or missing result has none).
export const programsOf = (item) => {
  const list = item?.results?.['cve.topSoftware'];
  return Array.isArray(list) ? list.filter(p => p && p.name) : [];
};

// A program is one row by its winget id when the machine reported one (cve.topSoftware carries id and the catalog's name as displayName),
// otherwise by its family name. A program with no id whose family name is the name of an id row joins that row, so one product does not
// split in two because winget linked it on some machines and not on others.
const lower = (v) => String(v || '').toLowerCase();
const labelOf = (p) => (p.id ? (p.displayName || familyOf(p.name)) : familyOf(p.name));
const makeKeyOf = (programs) => {
  const idRowByName = new Map();
  programs.forEach((p) => { if (p.id) idRowByName.set(lower(labelOf(p)), `id:${lower(p.id)}`); });
  return (p) => (p.id ? `id:${lower(p.id)}` : (idRowByName.get(lower(familyOf(p.name))) || `name:${familyOf(p.name)}`));
};

// This machine's programs of one row (a row of softwareFamilies).
export const familyEntries = (item, row) => programsOf(item).filter(p => row?.has(p));

// One row per program across the machines, worst first: critical, then high, then the highest score, then how many
// machines. Critical and high are the largest count of any one installed version (the same CVE is on every machine
// that has the version, so adding them up would count it again and again). A row is {key, family (the name shown), has(program), ...}.
export const softwareFamilies = (items) => {
  const families = new Map();
  const keyOf = makeKeyOf((items || []).flatMap(programsOf));
  (items || []).forEach((item) => {
    programsOf(item).forEach((p) => {
      const key = keyOf(p);
      const row = families.get(key) || {
        key, family: labelOf(p), wingetId: '', has: (q) => keyOf(q) === key, machines: new Set(), unusedMachines: new Set(), versions: new Set(), critical: 0, high: 0, highestScore: 0,
      };
      if (p.id && p.displayName) row.family = p.displayName;
      if (p.id && !row.wingetId) row.wingetId = String(p.id);
      row.machines.add(item.deviceId);
      if (p.unused) row.unusedMachines.add(item.deviceId);
      if (p.version) row.versions.add(String(p.version));
      row.critical = Math.max(row.critical, num(p.critical));
      row.high = Math.max(row.high, num(p.high));
      row.highestScore = Math.max(row.highestScore, num(p.highestScore));
      families.set(key, row);
    });
  });
  return [...families.values()]
    .map(r => ({
      key: r.key,
      has: r.has,
      wingetId: r.wingetId,
      family: r.family,
      machines: r.machines.size,
      unusedMachines: r.unusedMachines.size,
      versions: [...r.versions].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      critical: r.critical,
      high: r.high,
      highestScore: r.highestScore,
    }))
    .sort((a, b) => b.critical - a.critical || b.high - a.high || b.highestScore - a.highestScore
      || b.machines - a.machines || a.family.localeCompare(b.family));
};

// What to do about a family: remove it where nobody uses it, otherwise update it.
export const actionHint = (row) => {
  if (!row || row.unusedMachines <= 0) return 'Update';
  const where = row.unusedMachines === row.machines
    ? (row.machines === 1 ? 'the machine' : `all ${row.machines} machines`)
    : `${row.unusedMachines} of ${row.machines} machines`;
  return `Remove: unused on ${where}`;
};

// A family as a tile-like object (the summary page lists the machines behind the clicked one).
export const familyTile = (row) => ({
  id: row.key,
  label: row.family,
  count: row.machines,
  color: row.critical > 0 ? 'error' : row.high > 0 ? 'warning' : 'info',
  matches: (item) => familyEntries(item, row).length > 0,
});
